import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { locationsService, CountryOption, StateOption, CityOption } from '../../../services/Locations/locationsService';

export interface LocationValue {
  city: string;
  state: string;
  country: string;
}

export interface LocationSelectorProps {
  value?: Partial<LocationValue> | null;
  onChange: (location: LocationValue) => void;
  onBlur?: () => void;
  hasError?: boolean;
  disabled?: boolean;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  value,
  onChange,
  onBlur,
  hasError,
  disabled,
}) => {
  const city = value?.city ?? '';
  const state = value?.state ?? '';
  const country = value?.country ?? '';

  // null = still loading/unknown, [] = confirmed empty (e.g. a country with
  // no state subdivisions in the dataset, like Singapore or Monaco).
  const [countries, setCountries] = useState<CountryOption[] | null>(null);
  const [states, setStates] = useState<StateOption[] | null>(null);
  const [cities, setCities] = useState<CityOption[] | null>(null);

  // The stored value shape is plain names (matches the existing `city`/
  // `state`/`country` text columns in the DB), while the backend keys
  // everything off ISO codes — so every lookup below resolves name -> code
  // and every emit resolves the selected code back to a name.
  const countryList = useMemo(() => {
    const list = countries ?? [];
    return [...list.filter((c) => c.isoCode === 'US'), ...list.filter((c) => c.isoCode !== 'US')];
  }, [countries]);

  // Match by name OR isoCode — some existing rows were saved by an earlier
  // version of this selector that stored the state as a code (e.g. "TX")
  // instead of a full name ("Texas").
  const selectedCountry = countryList.find((c) => c.name === country || c.isoCode === country);
  const countryCode = selectedCountry?.isoCode ?? '';

  const stateList = states ?? [];
  const selectedState = stateList.find((s) => s.name === state || s.isoCode === state);
  const stateCode = selectedState?.isoCode ?? '';

  const cityList = cities ?? [];

  useEffect(() => {
    let cancelled = false;
    locationsService.getCountries().then((list) => {
      if (!cancelled) setCountries(list);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!countryCode) {
      setStates([]);
      return;
    }
    let cancelled = false;
    setStates(null);
    locationsService.getStates(countryCode).then((list) => {
      if (!cancelled) setStates(list);
    });
    return () => { cancelled = true; };
  }, [countryCode]);

  useEffect(() => {
    if (!countryCode || states === null) {
      setCities(countryCode ? null : []);
      return;
    }
    if (states.length > 0 && !stateCode) {
      setCities([]);
      return;
    }
    let cancelled = false;
    setCities(null);
    locationsService.getCities(countryCode, stateCode || undefined).then((list) => {
      if (!cancelled) setCities(list);
    });
    return () => { cancelled = true; };
  }, [countryCode, stateCode, states]);

  const emit = (next: Partial<LocationValue>) => {
    onChange({ city, state, country, ...next });
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = countryList.find((c) => c.isoCode === e.target.value);
    // Country change invalidates whatever state/city were selected under
    // the old country.
    emit({ country: next?.name ?? '', state: '', city: '' });
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = stateList.find((s) => s.isoCode === e.target.value);
    emit({ state: next?.name ?? '', city: '' });
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    emit({ city: e.target.value });
  };

  const countryDisabled = disabled || countries === null;
  const countryPlaceholder = countries === null ? 'Loading countries…' : 'Select country';

  const stateDisabled = disabled || !countryCode || states === null || states.length === 0;
  const statePlaceholder = !countryCode
    ? 'Select country first'
    : states === null
    ? 'Loading states…'
    : states.length === 0
    ? 'Not applicable'
    : 'Select state';

  const waitingOnState = states !== null && states.length > 0 && !stateCode;
  const citiesLoading = !!countryCode && !waitingOnState && cities === null;
  const cityDisabled = disabled || !countryCode || waitingOnState || citiesLoading || cityList.length === 0;
  const cityPlaceholder = !countryCode
    ? 'Select country first'
    : waitingOnState
    ? 'Select state first'
    : citiesLoading
    ? 'Loading cities…'
    : cityList.length === 0
    ? 'No cities found'
    : 'Select city';

  return (
    <LocationGrid>
      <LocationField>
        <FieldLabel>Country</FieldLabel>
        <LocationSelect
          aria-label="Country"
          value={countryCode}
          onChange={handleCountryChange}
          onBlur={onBlur}
          $hasError={hasError}
          disabled={countryDisabled}
        >
          <option value="">{countryPlaceholder}</option>
          {countryList.map((c) => (
            <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
          ))}
        </LocationSelect>
      </LocationField>

      <LocationField>
        <FieldLabel>State / Province</FieldLabel>
        <LocationSelect
          aria-label="State"
          value={stateCode}
          onChange={handleStateChange}
          onBlur={onBlur}
          $hasError={hasError}
          disabled={stateDisabled}
        >
          <option value="">{statePlaceholder}</option>
          {stateList.map((s) => (
            <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
          ))}
        </LocationSelect>
      </LocationField>

      <LocationField>
        <FieldLabel>City</FieldLabel>
        <LocationSelect
          aria-label="City"
          value={city}
          onChange={handleCityChange}
          onBlur={onBlur}
          $hasError={hasError}
          disabled={cityDisabled}
        >
          <option value="">{cityPlaceholder}</option>
          {cityList.map((c) => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </LocationSelect>
      </LocationField>
    </LocationGrid>
  );
};

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${theme.spacing.sm};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const LocationField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FieldLabel = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
`;

const LocationSelect = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.card};
  border: 1px solid ${props => props.$hasError ? theme.colors.error : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? `${theme.colors.error}20` : `${theme.colors.primary}20`};
  }

  option {
    background: ${theme.colors.background.card};
    color: ${theme.colors.text.primary};
  }
`;
